import { makeAutoObservable, computed, runInAction } from 'mobx';
import type { AuditLog } from './types';

export class AuditLogStore {
  auditLogs = new Map<string, AuditLog>();

  // Secondary index: entityId → Set of audit log IDs (plain, not observable)
  private _entityIdIndex = new Map<string, Set<string>>();

  constructor() {
    makeAutoObservable(this, {
      all: computed,
    });
  }

  // ── Index helpers ──────────────────────────────────────────────────────────

  private _addToIndex(entityId: string, id: string): void {
    if (!this._entityIdIndex.has(entityId)) this._entityIdIndex.set(entityId, new Set());
    this._entityIdIndex.get(entityId)!.add(id);
  }

  private _removeFromIndex(entityId: string, id: string): void {
    this._entityIdIndex.get(entityId)?.delete(id);
  }

  private _set(id: string, log: AuditLog): void {
    const existing = this.auditLogs.get(id);
    if (existing && existing.entityId !== log.entityId) {
      this._removeFromIndex(existing.entityId, id);
      this._addToIndex(log.entityId, id);
    } else if (!existing) {
      this._addToIndex(log.entityId, id);
    }
    this.auditLogs.set(id, log);
  }

  private _delete(id: string): void {
    const existing = this.auditLogs.get(id);
    if (existing) this._removeFromIndex(existing.entityId, id);
    this.auditLogs.delete(id);
  }

  // ── Computed ───────────────────────────────────────────────────────────────

  get all(): AuditLog[] {
    const result: AuditLog[] = [];
    for (const a of this.auditLogs.values()) {
      if (!a.isDeleted) result.push(a);
    }
    return result;
  }

  // ── Queries ────────────────────────────────────────────────────────────────

  getById(id: string): AuditLog | undefined {
    const log = this.auditLogs.get(id);
    return log && !log.isDeleted ? log : undefined;
  }

  getByEntityId(entityId: string): AuditLog[] {
    const ids = this._entityIdIndex.get(entityId);
    if (!ids || ids.size === 0) return [];
    const result: AuditLog[] = [];
    for (const id of ids) {
      const a = this.auditLogs.get(id);
      if (a && !a.isDeleted) result.push(a);
    }
    return result;
  }

  getByEntityIds(entityIds: Set<string>): AuditLog[] {
    const result: AuditLog[] = [];
    for (const entityId of entityIds) {
      const ids = this._entityIdIndex.get(entityId);
      if (!ids) continue;
      for (const id of ids) {
        const a = this.auditLogs.get(id);
        if (a && !a.isDeleted) result.push(a);
      }
    }
    return result;
  }

  getByEntityType(entityType: string): AuditLog[] {
    return this.all.filter(a => a.entityType === entityType);
  }

  // ── Bootstrap ──────────────────────────────────────────────────────────────

  loadFromRecords(records: Record<string, unknown>[]): void {
    runInAction(() => {
      for (const r of records) {
        this._set(r.id as string, r as unknown as AuditLog);
      }
    });
  }

  // ── Sync ───────────────────────────────────────────────────────────────────

  applyChanges(changes: { id: string; data?: Record<string, unknown>; isDeleted: boolean }[]): void {
    runInAction(() => {
      for (const c of changes) {
        if (c.isDeleted) this._delete(c.id);
        else if (c.data) this._set(c.id, c.data as unknown as AuditLog);
      }
    });
  }

  removeFromMap(id: string): void {
    this._delete(id);
  }

  clear(): void {
    this.auditLogs.clear();
    this._entityIdIndex.clear();
  }
}
